/**
 * Phase 27.45 + 27.46 — Marketing Reports tab.
 *
 * Container with a Source toggle (Event Requests · Retail Program).
 * Individual report views live in child components below.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, BarChart3, DollarSign, Users, TrendingUp, Building2,
  LayoutGrid, Store, MapPin, PieChart, CheckCircle2, XCircle, Download,
} from 'lucide-react';
import { eventRequestsAPI, retailProgramAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtPct(v) {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toFixed(1)}%`;
}

export default function EventReportsTab() {
  const [source, setSource] = useState('event_requests');
  return (
    <div className="space-y-4" data-testid="marketing-reports-tab">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          Source:
        </span>
        <Button
          variant={source === 'event_requests' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSource('event_requests')}
          data-testid="reports-source-events"
        >
          <BarChart3 size={13} className="mr-1" /> Event Requests
        </Button>
        <Button
          variant={source === 'retail_program' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSource('retail_program')}
          data-testid="reports-source-retail"
        >
          <Store size={13} className="mr-1" /> CVS Retail Program
        </Button>
        <Button
          variant={source === 'venues' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSource('venues')}
          data-testid="reports-source-venues"
        >
          <MapPin size={13} className="mr-1" /> Table Top / Marketing Site
        </Button>
      </div>
      {source === 'event_requests'
        ? <EventRequestsReport />
        : <RetailProgramReport key={source} venues={source === 'venues'} />}
    </div>
  );
}

function EventRequestsReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const r = await eventRequestsAPI.reportOverview(params);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const events = data?.events || [];

  // Phase 27.45 Phase 4 — Aggregate the flat event list into By-Agency
  // and By-Event-Type breakdowns. Kept client-side to avoid a second
  // round-trip; the overview endpoint already returns everything we
  // need per row.
  const byAgency = useMemo(() => {
    const acc = new Map();
    events.forEach((e) => {
      const key = e.requester_agency_id || 'no_agency';
      const label = e.requester_agency_id || 'Direct / KR';
      const row = acc.get(key) || {
        key, label, num_events: 0, cost: 0, approved: 0,
        leads: 0, closed: 0,
      };
      row.num_events += 1;
      row.cost += Number(e.total_event_cost || 0);
      row.approved += Number(e.amount_approved || 0);
      row.leads += Number(e.leads_generated || 0);
      row.closed += Number(e.leads_closed || 0);
      acc.set(key, row);
    });
    return Array.from(acc.values()).sort((a, b) => b.approved - a.approved);
  }, [events]);

  const byType = useMemo(() => {
    const acc = new Map();
    events.forEach((e) => {
      const key = e.event_type || 'other';
      const row = acc.get(key) || {
        key, num_events: 0, cost: 0, approved: 0, leads: 0, closed: 0,
      };
      row.num_events += 1;
      row.cost += Number(e.total_event_cost || 0);
      row.approved += Number(e.amount_approved || 0);
      row.leads += Number(e.leads_generated || 0);
      row.closed += Number(e.leads_closed || 0);
      acc.set(key, row);
    });
    return Array.from(acc.values()).sort((a, b) => b.leads - a.leads);
  }, [events]);

  const kpis = [
    { label: 'Events', value: totals.num_events || 0, icon: BarChart3 },
    { label: 'Total Cost', value: fmtMoney(totals.total_event_cost), icon: DollarSign },
    { label: 'Approved Funding', value: fmtMoney(totals.total_amount_approved), icon: DollarSign, emphasis: true },
    { label: 'Projected Sales', value: `${Number(totals.total_projected_sales || 0).toLocaleString()} sales`, icon: TrendingUp },
    { label: 'Participating Agents', value: totals.num_participating_agents || 0, icon: Users },
  ];

  return (
    <div data-testid="event-reports-tab" className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          Filter by event date
        </span>
        <Input
          type="date" value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="w-40" data-testid="report-from-date"
        />
        <Input
          type="date" value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="w-40" data-testid="report-to-date"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.label}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    {k.label}
                  </p>
                  <p className={`text-xl font-bold ${k.emphasis ? 'text-emerald-700' : 'text-slate-900'}`}>
                    {k.value}
                  </p>
                </div>
                <Icon className={k.emphasis ? 'text-emerald-300' : 'text-slate-300'} size={22} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <Loader2 className="animate-spin text-slate-400" />
            </div>
          ) : events.length === 0 ? (
            <div className="py-16 text-center">
              <BarChart3 className="mx-auto mb-3 text-slate-300" size={32} />
              <p className="text-slate-500 font-medium">No event data in this window</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-semibold">Event</th>
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold text-right">Cost</th>
                    <th className="px-4 py-3 font-semibold text-right">Approved</th>
                    <th className="px-4 py-3 font-semibold text-right">Leads</th>
                    <th className="px-4 py-3 font-semibold text-right">Closed</th>
                    <th className="px-4 py-3 font-semibold text-right">Conv.</th>
                    <th className="px-4 py-3 font-semibold text-right">Cost / Lead</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {events.map((e) => (
                    <tr key={e.request_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 truncate max-w-[200px]">
                          {e.event_name}
                        </div>
                        <div className="text-[11px] text-slate-500 capitalize">
                          {(e.event_type || '').replace(/_/g, ' ')}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDate(e.event_date)}</td>
                      <td className="px-4 py-3 capitalize text-slate-700">
                        {(e.status || '').replace(/_/g, ' ')}
                      </td>
                      <td className="px-4 py-3 text-right">{fmtMoney(e.total_event_cost)}</td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-700">
                        {fmtMoney(e.amount_approved)}
                      </td>
                      <td className="px-4 py-3 text-right">{e.leads_generated || 0}</td>
                      <td className="px-4 py-3 text-right">{e.leads_closed || 0}</td>
                      <td className="px-4 py-3 text-right">
                        {e.leads_generated
                          ? `${((e.leads_closed / e.leads_generated) * 100).toFixed(0)}%`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {e.cost_per_lead !== null && e.cost_per_lead !== undefined
                          ? fmtMoney(e.cost_per_lead) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-slate-400 italic">
        Note: Insurance commission revenue is intentionally not calculated here — compensation
        varies by product and carrier. Effectiveness is measured via leads, closed sales and
        cost per lead / per closed sale.
      </p>

      {/* By Agency breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="report-by-agency">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Building2 size={14} className="text-slate-500" />
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                By Agency
              </p>
            </div>
            {byAgency.length === 0 ? (
              <p className="p-6 text-center text-xs text-slate-400 italic">No data.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Agency</th>
                    <th className="text-right px-3 py-2 font-semibold">Events</th>
                    <th className="text-right px-3 py-2 font-semibold">Approved</th>
                    <th className="text-right px-3 py-2 font-semibold">Leads</th>
                    <th className="text-right px-3 py-2 font-semibold">Closed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byAgency.map((a) => (
                    <tr key={a.key}>
                      <td className="px-3 py-2 font-medium text-slate-900 truncate max-w-[140px]" title={a.label}>
                        {a.label}
                      </td>
                      <td className="px-3 py-2 text-right">{a.num_events}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-medium">
                        {fmtMoney(a.approved)}
                      </td>
                      <td className="px-3 py-2 text-right">{a.leads}</td>
                      <td className="px-3 py-2 text-right">{a.closed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card data-testid="report-by-type">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <LayoutGrid size={14} className="text-slate-500" />
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                By Event Type
              </p>
            </div>
            {byType.length === 0 ? (
              <p className="p-6 text-center text-xs text-slate-400 italic">No data.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Type</th>
                    <th className="text-right px-3 py-2 font-semibold">Events</th>
                    <th className="text-right px-3 py-2 font-semibold">Approved</th>
                    <th className="text-right px-3 py-2 font-semibold">Leads</th>
                    <th className="text-right px-3 py-2 font-semibold">Closed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byType.map((t) => (
                    <tr key={t.key}>
                      <td className="px-3 py-2 font-medium text-slate-900 capitalize">
                        {t.key.replace(/_/g, ' ')}
                      </td>
                      <td className="px-3 py-2 text-right">{t.num_events}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-medium">
                        {fmtMoney(t.approved)}
                      </td>
                      <td className="px-3 py-2 text-right">{t.leads}</td>
                      <td className="px-3 py-2 text-right">{t.closed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Retail Program report ─────────────────────────────────────────

const PROGRAM_LABELS = { cvs: 'CVS', table_top: 'Table Top', marketing_site: 'Marketing Site' };

const to12hLbl = (t) => {
  if (!t || !t.includes(':')) return t || '';
  const [h, m] = t.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return t;
  const ap = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ap}`;
};

function RetailProgramReport({ venues = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [carrier, setCarrier] = useState('all');
  const [program, setProgram] = useState(venues ? 'venues' : 'all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (carrier !== 'all') params.carrier = carrier;
      if (program !== 'all') params.program_type = program;
      const r = await retailProgramAPI.reportsOverview(params);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load retail report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, carrier, program]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const byCarrier = data?.by_carrier || [];
  const byProgram = data?.by_program || [];
  const byStore = data?.by_store || [];
  const byAgent = data?.by_agent || [];
  const byAgency = data?.by_agency || [];
  const assignments = data?.assignments || [];

  const exportCsv = () => {
    const header = ['Program', 'Carrier', 'Location', 'Address', 'City', 'State', 'Date', 'Start', 'End', 'Agent', 'Leads', 'Closed'];
    const rows = assignments.map((a) => [
      PROGRAM_LABELS[a.program_type] || a.program_type || '',
      a.carrier || '',
      a.location_name || '',
      a.store_address || '',
      a.city || '',
      a.state || '',
      a.date || '',
      to12hLbl(a.start),
      to12hLbl(a.end),
      a.agent_name || '',
      a.leads ?? 0,
      a.closed ?? 0,
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${venues ? 'tabletop-marketingsite' : 'retail-cvs'}-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const kpis = [
    { label: 'Requests', value: totals.requests_submitted || 0, icon: BarChart3 },
    { label: 'Slots Requested', value: totals.slots_requested || 0, icon: LayoutGrid },
    { label: 'Slots Approved', value: (totals.slots_approved || 0) + (totals.slots_modified || 0), icon: CheckCircle2, emphasis: true },
    { label: 'Slots Declined', value: totals.slots_declined || 0, icon: XCircle },
    { label: 'Approval Rate', value: fmtPct(totals.approval_rate), icon: PieChart },
    { label: 'Active Stores', value: totals.active_stores || 0, icon: Store },
    { label: 'Leads Generated', value: totals.leads_generated || 0, icon: Users },
    { label: 'Conversion Rate', value: fmtPct(totals.conversion_rate), icon: TrendingUp },
  ];

  return (
    <div className="space-y-5" data-testid="retail-report">
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center flex-wrap">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          Filter
        </span>
        <Input
          type="date" value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="w-40" data-testid="retail-report-from-date"
        />
        <Input
          type="date" value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="w-40" data-testid="retail-report-to-date"
        />
        <div className="flex gap-1">
          {['all', 'devoted', 'solis', 'elevance'].map((c) => (
            <Button
              key={c}
              variant={carrier === c ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCarrier(c)}
              data-testid={`retail-report-carrier-${c}`}
            >
              {c === 'all' ? 'All' : c[0].toUpperCase() + c.slice(1)}
            </Button>
          ))}
        </div>
        <div className="flex gap-1">
          {(venues
            ? [['venues', 'All venues'], ['table_top', 'Table Top'], ['marketing_site', 'Marketing Site']]
            : [['all', 'All programs'], ['cvs', 'CVS'], ['table_top', 'Table Top'], ['marketing_site', 'Marketing Site']]
          ).map(([v, lbl]) => (
            <Button
              key={v}
              variant={program === v ? 'default' : 'outline'}
              size="sm"
              onClick={() => setProgram(v)}
              data-testid={`retail-report-program-${v}`}
            >
              {lbl}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={exportCsv}
          disabled={assignments.length === 0}
          className="ml-auto"
          data-testid="retail-report-export-csv"
        >
          <Download size={13} className="mr-1" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.label}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    {k.label}
                  </p>
                  <p className={`text-xl font-bold ${k.emphasis ? 'text-emerald-700' : 'text-slate-900'}`}>
                    {k.value}
                  </p>
                </div>
                <Icon className={k.emphasis ? 'text-emerald-300' : 'text-slate-300'} size={22} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {loading && (
        <div className="py-16 flex items-center justify-center">
          <Loader2 className="animate-spin text-slate-400" />
        </div>
      )}

      {!loading && (
        <>
          {/* By Program */}
          <Card data-testid="retail-report-by-program">
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <LayoutGrid size={14} className="text-slate-500" />
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  By Program
                </p>
              </div>
              {byProgram.length === 0 ? (
                <p className="p-6 text-center text-xs text-slate-400 italic">No data.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Program</th>
                      <th className="text-right px-3 py-2 font-semibold">Requests</th>
                      <th className="text-right px-3 py-2 font-semibold">Requested</th>
                      <th className="text-right px-3 py-2 font-semibold">Approved</th>
                      <th className="text-right px-3 py-2 font-semibold">Declined</th>
                      <th className="text-right px-3 py-2 font-semibold">Approval</th>
                      <th className="text-right px-3 py-2 font-semibold">Leads</th>
                      <th className="text-right px-3 py-2 font-semibold">Closed</th>
                      <th className="text-right px-3 py-2 font-semibold">Conv.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {byProgram.map((p) => (
                      <tr key={p.program_type}>
                        <td className="px-3 py-2 font-medium text-slate-900">{PROGRAM_LABELS[p.program_type] || p.program_type}</td>
                        <td className="px-3 py-2 text-right">{p.requests}</td>
                        <td className="px-3 py-2 text-right">{p.slots_requested}</td>
                        <td className="px-3 py-2 text-right text-emerald-700 font-medium">
                          {(p.slots_approved || 0) + (p.slots_modified || 0)}
                        </td>
                        <td className="px-3 py-2 text-right text-rose-700">{p.slots_declined}</td>
                        <td className="px-3 py-2 text-right">{fmtPct(p.approval_rate)}</td>
                        <td className="px-3 py-2 text-right">{p.leads}</td>
                        <td className="px-3 py-2 text-right">{p.closed}</td>
                        <td className="px-3 py-2 text-right">{fmtPct(p.conversion_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* By Carrier */}
          <Card data-testid="retail-report-by-carrier">
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <PieChart size={14} className="text-slate-500" />
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  By Carrier
                </p>
              </div>
              {byCarrier.length === 0 ? (
                <p className="p-6 text-center text-xs text-slate-400 italic">No data.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Carrier</th>
                      <th className="text-right px-3 py-2 font-semibold">Requests</th>
                      <th className="text-right px-3 py-2 font-semibold">Requested</th>
                      <th className="text-right px-3 py-2 font-semibold">Approved</th>
                      <th className="text-right px-3 py-2 font-semibold">Declined</th>
                      <th className="text-right px-3 py-2 font-semibold">Approval</th>
                      <th className="text-right px-3 py-2 font-semibold">Leads</th>
                      <th className="text-right px-3 py-2 font-semibold">Closed</th>
                      <th className="text-right px-3 py-2 font-semibold">Conv.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {byCarrier.map((c) => (
                      <tr key={c.carrier}>
                        <td className="px-3 py-2 font-medium text-slate-900 capitalize">{c.carrier}</td>
                        <td className="px-3 py-2 text-right">{c.requests}</td>
                        <td className="px-3 py-2 text-right">{c.slots_requested}</td>
                        <td className="px-3 py-2 text-right text-emerald-700 font-medium">
                          {(c.slots_approved || 0) + (c.slots_modified || 0)}
                        </td>
                        <td className="px-3 py-2 text-right text-rose-700">{c.slots_declined}</td>
                        <td className="px-3 py-2 text-right">{fmtPct(c.approval_rate)}</td>
                        <td className="px-3 py-2 text-right">{c.leads}</td>
                        <td className="px-3 py-2 text-right">{c.closed}</td>
                        <td className="px-3 py-2 text-right">{fmtPct(c.conversion_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* By Store + By Agent */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card data-testid="retail-report-by-store">
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  <MapPin size={14} className="text-slate-500" />
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    By Store
                  </p>
                </div>
                {byStore.length === 0 ? (
                  <p className="p-6 text-center text-xs text-slate-400 italic">No data.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Store</th>
                        <th className="text-right px-3 py-2 font-semibold">Assign.</th>
                        <th className="text-right px-3 py-2 font-semibold">Leads</th>
                        <th className="text-right px-3 py-2 font-semibold">Closed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {byStore.slice(0, 10).map((s, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2">
                            <div className="text-slate-900 truncate max-w-[180px]" title={s.location_name || s.store_address}>
                              {s.location_name || s.store_address}
                            </div>
                            <div className="text-[10px] text-slate-500 capitalize">
                              {s.city}, {s.state} · {PROGRAM_LABELS[s.program_type] || s.carrier}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">{s.assignments}</td>
                          <td className="px-3 py-2 text-right">{s.leads}</td>
                          <td className="px-3 py-2 text-right">{s.closed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <Card data-testid="retail-report-by-agent">
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  <Users size={14} className="text-slate-500" />
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    By Agent
                  </p>
                </div>
                {byAgent.length === 0 ? (
                  <p className="p-6 text-center text-xs text-slate-400 italic">No data.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Agent</th>
                        <th className="text-right px-3 py-2 font-semibold">Assign.</th>
                        <th className="text-right px-3 py-2 font-semibold">Leads</th>
                        <th className="text-right px-3 py-2 font-semibold">Closed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {byAgent.slice(0, 10).map((a) => (
                        <tr key={a.agent_npn}>
                          <td className="px-3 py-2">
                            <div className="text-slate-900 truncate max-w-[160px]">
                              {a.agent_name || '—'}
                            </div>
                            <div className="text-[10px] text-slate-500">NPN {a.agent_npn}</div>
                          </td>
                          <td className="px-3 py-2 text-right">{a.assignments}</td>
                          <td className="px-3 py-2 text-right">{a.leads}</td>
                          <td className="px-3 py-2 text-right">{a.closed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* By Assignment */}
          <Card data-testid="retail-report-assignments">
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <LayoutGrid size={14} className="text-slate-500" />
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  By Retail Assignment
                </p>
              </div>
              {assignments.length === 0 ? (
                <p className="p-6 text-center text-xs text-slate-400 italic">
                  No approved assignments yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Program</th>
                        <th className="text-left px-3 py-2 font-semibold">Store / Location</th>
                        <th className="text-left px-3 py-2 font-semibold">Agent</th>
                        <th className="text-left px-3 py-2 font-semibold">Date</th>
                        <th className="text-right px-3 py-2 font-semibold">Leads</th>
                        <th className="text-right px-3 py-2 font-semibold">Closed</th>
                        <th className="text-right px-3 py-2 font-semibold">Conv.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {assignments.map((a) => (
                        <tr key={a.retail_assignment_id} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-900">
                            {PROGRAM_LABELS[a.program_type] || a.carrier}
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-slate-800 truncate max-w-[200px]" title={a.location_name || a.store_address}>
                              {a.location_name || a.store_address}
                            </div>
                            <div className="text-[10px] text-slate-500">{a.city}, {a.state}</div>
                          </td>
                          <td className="px-3 py-2 text-slate-800">{a.agent_name || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {fmtDate(a.date)}
                            <span className="text-[10px] text-slate-500 block">
                              {to12hLbl(a.start)} – {to12hLbl(a.end)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">{a.leads}</td>
                          <td className="px-3 py-2 text-right">{a.closed}</td>
                          <td className="px-3 py-2 text-right">
                            {a.leads
                              ? `${((a.closed / a.leads) * 100).toFixed(0)}%`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-[11px] text-slate-400 italic">
            Note: Financial ROI is intentionally not calculated for Retail Program —
            no funding or reimbursements are attached. Effectiveness = leads generated,
            closed sales and conversion rate per store / carrier / agent.
          </p>
        </>
      )}
    </div>
  );
}

